import "./ParameterValues.css";
import DataFilters from "./DataFilters";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChartBar, ChartLineUp, CheckCircle, Clock, Crosshair, Funnel, ShieldCheck, Star, UsersThree, X, Table, UploadSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import CommunityDatasetCard from "@/components/CommunityDatasetCard";
import ConsensusPanel from "@/components/ConsensusPanel";
import ComparisonModal from "@/components/ComparisonModal";

const SORT_OPTIONS = [
    { key: "highest_agreement", label: "Highest Agreement" },
    { key: "highest_rating", label: "Highest Rating" },
    { key: "expert_validated", label: "Expert Validated" },
    { key: "most_recent", label: "Most Recent" },
    { key: "oldest", label: "Oldest" },
    { key: "highest_confidence", label: "Highest Confidence" },
    { key: "lowest_confidence", label: "Lowest Confidence" },
    { key: "value_low_high", label: "Value (low → high)" },
    { key: "value_high_low", label: "Value (high → low)" },
    { key: "peer_reviewed", label: "Peer Reviewed First" },
];

const ORG_TYPES = [
    { key: "all", label: "All sources" },
    { key: "government", label: "Government" },
    { key: "industry", label: "Industry" },
    { key: "academic", label: "Academic" },
    { key: "journal", label: "Peer-reviewed" },
    { key: "ngo", label: "NGO" },
    { key: "consultant", label: "Consultant" },
    { key: "independent", label: "Independent" },
];

const STATUS_OPTIONS = [
    { key: "all", label: "Any status" },
    { key: "published", label: "Published" },
    { key: "expert_validated", label: "Expert Validated" },
    { key: "under_review", label: "Under Review" },
    { key: "pending_expert", label: "Pending Expert" },
    { key: "retracted", label: "Retracted" },
    { key: "archived", label: "Archived" },
];

const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 20 });

export default function ParameterDetail({ selection, concept, onBack, backLabel = "Parameters", entryId, filters = {}, onFiltersChange }) {
    const setFilters = onFiltersChange;
    const [datasetCache, setDatasetCache] = useState(new Map());
    const [entrySearch, setEntrySearch] = useState("");
    const [loadError, setLoadError] = useState("");
    const [datasets, setDatasets] = useState([]);
    const [consensus, setConsensus] = useState(null);
    const [loading, setLoading] = useState(true);

    const [sort, setSort] = useState("highest_agreement");

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [compareOpen, setCompareOpen] = useState(false);

    useEffect(() => {
        endpoints.conceptConsensus(concept.id).then(setConsensus).catch(() => setConsensus(null));
    }, [concept.id]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError("");
        const params = { sort };
        endpoints
            .conceptDatasets(concept.id, params)
            .then(data => { if (active) { setDatasets(data); setDatasetCache(previous => new Map([...previous, ...data.map(d => [d.id, d])])); } })
            .catch(() => { if (active) setLoadError("Entries could not be loaded. Please try again."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [concept.id, sort]);

    const toggleCompare = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else if (next.size < 5) next.add(id);
            return next;
        });
    };

    const selectedDatasets = useMemo(
        () => [...datasetCache.values()].filter((d) => selectedIds.has(d.id)),
        [datasetCache, selectedIds]
    );

    const visibleDatasets = datasets.filter(d => !filters.geography || d.geography === filters.geography).filter(d => !entrySearch.trim() || Object.entries(d).filter(([key]) => !/email|password|token|secret/i.test(key)).some(([,value]) => String(value ?? "").toLowerCase().includes(entrySearch.trim().toLowerCase())));

    return (
        <div data-testid={TEST_IDS.parameterDetail} className="parameter-values pb-12">
            {/* Breadcrumb + back */}
            <div className="px-5 lg:px-7 pt-4 pb-4 flex items-center gap-3 sticky top-16 bg-white/95 backdrop-blur z-20">
                <button
                    onClick={onBack}
                    className="text-[11px] inline-flex items-center gap-1 text-neutral-500 hover:text-black"
                >
                    <ArrowLeft size={12} /> {backLabel}
                </button>
                <nav
                    data-testid={TEST_IDS.breadcrumb}
                    className="text-[11px] text-neutral-500 flex flex-wrap items-center gap-2"
                >
                    <span>{selection.country.flag}</span>
                    <span>{selection.country.name}</span>
                    <span className="text-neutral-300">/</span>
                    <span>{selection.category.name}</span>
                    <span className="text-neutral-300">/</span>
                    <span>{selection.material.name}</span>
                    <span className="text-neutral-300">/</span>
                    <span>{selection.material_type.name}</span>
                    <span className="text-neutral-300">/</span>
                    <span>{selection.process.name}</span>
                    <span className="text-neutral-300">/</span>
                    <span className="text-black font-medium">{concept.name}</span>
                </nav>
            </div>

            {/* Overview */}
            <section
                data-testid={TEST_IDS.parameterOverview}
                className="px-5 lg:px-7 pb-5"
            >
                <div className="py-1">
                        <Link to={`/explore?parameter=${encodeURIComponent(concept.id)}`} className="float-right text-xs text-violet-700 underline">Parameter permalink</Link>
                        <div className="label-caps text-[#6B46C1]">{concept.category}</div>
                        <h1 className="font-sans text-4xl md:text-5xl font-semibold tracking-tight mt-2 leading-none">
                            {concept.name}
                        </h1>
                        <div className="mt-3 text-xs text-neutral-500 leading-relaxed max-w-2xl">
                            <span className="text-black font-medium">{selection.material_type.name} - {selection.process.name}</span>
                            <span className="mx-2 text-neutral-300">·</span>
                            Compare submitted values and review their evidence in <span className="text-black">{selection.country.name}</span>.
                            Multiple organisations may submit different values — all remain
                            visible, versioned and comparable.
                        </div>
                    </div>

                <div className="value-summary">
                        <HighlightStat icon={Crosshair}
                            label="Most accepted"
                            value={consensus?.most_accepted?.value == null ? "Not yet reviewed" : nf.format(consensus.most_accepted.value)}
                            unit={consensus?.most_accepted ? concept.default_unit : ""}
                            note={consensus?.most_accepted?.organization_name || "Requires source evidence and at least 3 votes; not independent verification."}
                            tone="violet"
                        />
                        <HighlightStat icon={ChartLineUp} label="Median" value={concept.median_value == null ? "—" : nf.format(concept.median_value)} unit={concept.default_unit} tone="green" />
                        <HighlightStat icon={ChartBar} label="Range" value={concept.min_value == null ? "—" : `${nf.format(concept.min_value)}–${nf.format(concept.max_value)}`} unit={concept.default_unit} tone="amber" />
                </div>

                <details className="mt-5">
                <summary className="text-sm text-violet-700 cursor-pointer">Community statistics & distribution</summary>
                {/* Summary chips */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <SummaryChip icon={CheckCircle} label="Published" value={concept.published_count} tone="green" />
                    <SummaryChip icon={ShieldCheck} label="Expert Validated" value={concept.expert_validated_count} tone="blue" />
                    <SummaryChip icon={Clock} label="Under Review" value={concept.under_review_count} tone="yellow" />
                    <SummaryChip icon={UsersThree} label="Consensus" value={concept.consensus_score == null ? "Not yet reviewed" : `${concept.consensus_score}%`} tone="purple" />
                    <SummaryChip icon={Star} label="Community Rating" value={concept.consensus_score == null || concept.community_rating == null ? "Not yet reviewed" : `${concept.community_rating.toFixed(1)} ★`} tone="amber" />
                    <SummaryChip icon={UploadSimple} label="Total Submissions" value={concept.submission_count} tone="neutral" />
                </div>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-1 gap-5">
                <div className="lg:col-span-1">
                    <ConsensusPanel consensus={consensus} unit={concept.default_unit} />
                </div>

            </div>
                </details>
            </section>

            <div className="px-5 lg:px-7"><DataFilters value={filters} onChange={setFilters} onClear={() => setFilters({})} facets={{ years: [...new Set([...datasetCache.values()].map(d => d.year).filter(Boolean))].sort((a,b) => b-a), geographies: [...new Set([...datasetCache.values()].map(d => d.geography).filter(Boolean))].sort() }} /></div>
            {/* Datasets header & sort */}
            <section className="px-5 lg:px-7">
                <div className="border border-neutral-200 bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="flex items-center justify-between px-4 py-3 gap-4 flex-wrap border-b border-neutral-200">
                    <div>
                        <h2 className="text-xl font-semibold">Community entries ({datasets.length})</h2>
                        <span className="ml-3 px-2 py-1 border border-neutral-200 rounded-md text-[10px] text-neutral-500">{loading ? "Loading…" : `${visibleDatasets.length} shown`}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input aria-label="Search community entries" placeholder="Search entries…" value={entrySearch} onChange={e => setEntrySearch(e.target.value)} className="border border-slate-200 rounded px-3 py-2 text-sm w-48" />
                        <div className="text-xs text-neutral-500">Sort by</div>
                        <select
                            data-testid={TEST_IDS.sortDropdown}
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="px-3 py-2 border border-neutral-200 rounded-md text-xs bg-white"
                        >
                            {SORT_OPTIONS.map((o) => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {visibleDatasets.length === 0 && !loading && !loadError && (
                    <div className="border border-dashed border-orange-300 bg-orange-50/40 p-8 text-center rounded-sm">
                        <div className="font-serif text-2xl">No matching submissions</div>
                        <p className="text-sm text-neutral-600 mt-1">
                            Try clearing filters, or contribute a trusted source.
                        </p>
                        <Link to="/submit" className="btn-secondary mt-4">
                            <UploadSimple size={14} weight="bold" /> Contribute a source
                        </Link>
                    </div>
                )}

                {loadError && <p role="alert" className="p-5 text-red-700">{loadError}</p>}
                {datasets.length < 2 && <div className="entry-grid entry-heading" aria-hidden="true"><span>Value</span><span>Source & contributor</span><span>Scope & year</span><span>Community review</span><span>Your review</span><span>Details</span></div>}
                <div className="divide-y divide-neutral-100">
                    {visibleDatasets.map((d, i) => (
                        <CommunityDatasetCard
                            key={d.id}
                            focused={entryId === d.id}
                            dataset={d}
                            summaryMode={datasets.length > 1}
                            unit={concept.default_unit}
                            comparable
                            rank={i}
                            isCompareSelected={selectedIds.has(d.id)}
                            onToggleCompare={() => toggleCompare(d.id)}
                        />
                    ))}
                </div></div>
                <p className="text-xs text-slate-500 mt-3">Review the source before voting. You can change your vote. Expand an entry for its complete source and value details.</p>
            </section>

            {/* Comparison sticky bar */}
            {selectedIds.size > 0 && (
                <div
                    data-testid={TEST_IDS.comparisonBar}
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#09090B] text-white px-5 py-3 rounded-sm shadow-brut-purple flex items-center gap-4 z-40"
                >
                    <span className="text-sm">
                        {selectedIds.size} selected ({selectedDatasets.filter(d => !visibleDatasets.some(v => v.id === d.id)).length} hidden by filters)
                    </span>
                    <button
                        data-testid={TEST_IDS.comparisonOpen}
                        disabled={selectedIds.size < 2}
                        onClick={() => setCompareOpen(true)}
                        className="btn-secondary py-1.5 px-3 text-xs"
                    >
                        <Table size={12} weight="bold" /> Compare
                    </button>
                    <button
                        onClick={() => setSelectedIds(new Set())}
                        className="text-white/60 hover:text-white"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {compareOpen && (
                <ComparisonModal
                    datasets={selectedDatasets}
                    unit={concept.default_unit}
                    onClose={() => setCompareOpen(false)}
                />
            )}
        </div>
    );
}

const FilterGroup = ({ label, children }) => (
    <div className="mt-4">
        <div className="label-caps mb-2">{label}</div>
        {children}
    </div>
);

const TONE = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    yellow: "bg-yellow-50 border-yellow-300 text-yellow-800",
    purple: "bg-[#F5F3FF] border-[#DDD6FE] text-[#4C1D95]",
    amber: "bg-[#FFFBEB] border-[#FFD60A] text-[#713F12]",
    neutral: "bg-white border-neutral-200 text-neutral-500",
};

const SummaryChip = ({ icon: Icon, label, value, tone }) => (
    <div className={`px-3 py-2 border rounded-lg flex items-center gap-2 bg-white ${TONE[tone] || TONE.purple}`}>
        <Icon size={18} weight="duotone" className="shrink-0" />
        <span><span className="block text-[9px] font-bold uppercase tracking-[0.14em]">{label}</span>
        <span className="block text-base text-neutral-900 font-medium leading-tight">{value}</span></span>
    </div>
);

const HighlightStat = ({ label, value, unit, note }) => (
    <div className="value-stat">
        <div className="value-stat-label">{label}</div>
        <div className="value-stat-number">{value}<span className="value-stat-unit">{unit}</span></div>
        {note && <div className="text-xs text-slate-500 mt-1 break-words">{note}</div>}
    </div>
);
